/**
 * Download Action Handler
 *
 * Implements the download action for mini-apps, allowing them to download
 * tabular data as CSV files to the user's computer.
 */

import {
  type ActionError,
  type ActionRequest,
  createActionRpcError,
} from "@upsidelabs/miniapp-sdk";
import { exportToCSV } from "@/lib/csv-export";
import { sanitizeFilename } from "@/lib/file-download";
import {
  type ColumnDefinition,
  DOWNLOAD_ACTION_METHOD,
  type DownloadActionResult,
} from "@/types/miniapp-download";
import type { UnifiedTableColumn } from "@/types/table-column";

/**
 * Normalized download params after validation.
 */
export type NormalizedDownloadParams = {
  data: unknown[];
  columns: ColumnDefinition[];
  filename: string;
};

/**
 * Result of normalizing download params - either valid params or an error.
 */
export type NormalizeDownloadResult = {
  normalizedParams: NormalizedDownloadParams | null;
  error: ActionError | null;
};

export type OnBeforeDownload = (
  req: NormalizedDownloadParams,
  action: ActionRequest,
) => boolean | Promise<boolean>;

export type OnDownload = (
  params: NormalizedDownloadParams,
  rowCount: number,
) => void;

export type OnAfterDownload = (
  req: NormalizedDownloadParams,
  action: ActionRequest,
) => void | Promise<void>;

/**
 * Validates and normalizes download params.
 * Returns normalized params or an error.
 */
export function normalizeDownloadParams(
  params: unknown,
  maxRows: number = 50000,
): NormalizeDownloadResult {
  if (params === null || typeof params !== "object") {
    return {
      normalizedParams: null,
      error: { reason: "invalid_payload", message: "Params must be an object" },
    };
  }

  const { data, columns, filename } = params as Record<string, unknown>;

  // Validate data is an array
  if (!Array.isArray(data)) {
    return {
      normalizedParams: null,
      error: { reason: "invalid_payload", message: "Data must be an array" },
    };
  }

  // Check max rows limit
  if (data.length > maxRows) {
    return {
      normalizedParams: null,
      error: {
        reason: "invalid_payload",
        message: `Data exceeds maximum allowed rows (${maxRows})`,
      },
    };
  }

  // Validate columns is an array
  if (!Array.isArray(columns)) {
    return {
      normalizedParams: null,
      error: {
        reason: "invalid_payload",
        message: "Columns must be an array",
      },
    };
  }

  // Validate columns is not empty
  if (columns.length === 0) {
    return {
      normalizedParams: null,
      error: {
        reason: "invalid_payload",
        message: "Columns array cannot be empty",
      },
    };
  }

  // Validate each column has required fields
  for (let i = 0; i < columns.length; i++) {
    const col = columns[i];
    if (col === null || typeof col !== "object") {
      return {
        normalizedParams: null,
        error: {
          reason: "invalid_payload",
          message: `Column at index ${i} must be an object`,
        },
      };
    }

    const { key, header } = col as Record<string, unknown>;

    if (typeof key !== "string" || key.trim().length === 0) {
      return {
        normalizedParams: null,
        error: {
          reason: "invalid_payload",
          message: `Column at index ${i} must have a non-empty 'key' string`,
        },
      };
    }

    if (typeof header !== "string" || header.trim().length === 0) {
      return {
        normalizedParams: null,
        error: {
          reason: "invalid_payload",
          message: `Column at index ${i} must have a non-empty 'header' string`,
        },
      };
    }
  }

  // Validate filename
  if (typeof filename !== "string" || filename.trim().length === 0) {
    return {
      normalizedParams: null,
      error: {
        reason: "invalid_payload",
        message: "Filename must be a non-empty string",
      },
    };
  }

  return {
    normalizedParams: {
      data,
      columns: columns as ColumnDefinition[],
      filename: sanitizeFilename(filename),
    },
    error: null,
  };
}

/**
 * Options for creating the download action handler.
 */
export interface CreateDownloadHandlerOptions {
  /**
   * Maximum number of rows allowed per download (default: 50000).
   */
  maxRows?: number;

  /**
   * Optional callback for logging/analytics when download occurs.
   */
  onDownload?: OnDownload;

  /**
   * Optional hook invoked before download. Return false to block.
   */
  onBeforeDownload?: OnBeforeDownload;

  /**
   * Optional hook invoked after download.
   */
  onAfterDownload?: OnAfterDownload;
}

/**
 * Creates a download action handler for use with bridge.handle().
 *
 * @example
 * ```ts
 * const handler = createDownloadHandler({ maxRows: 100000 });
 * bridge.handle("upside.miniapp.rpc.v1.download", handler);
 * ```
 */
export function createDownloadHandler(
  options: CreateDownloadHandlerOptions = {},
): (params: unknown, req?: ActionRequest) => Promise<DownloadActionResult> {
  const {
    maxRows = 50000,
    onBeforeDownload,
    onDownload,
    onAfterDownload,
  } = options;

  return async (
    params: unknown,
    req?: ActionRequest,
  ): Promise<DownloadActionResult> => {
    const { normalizedParams, error } = normalizeDownloadParams(
      params,
      maxRows,
    );
    if (error || !normalizedParams) {
      throw createActionRpcError(
        error ?? {
          reason: "invalid_payload",
          message: "Invalid download params",
        },
      );
    }

    const { data, columns, filename } = normalizedParams;

    if (onBeforeDownload) {
      const allowed = await onBeforeDownload(normalizedParams, {
        method: DOWNLOAD_ACTION_METHOD,
        payload: params,
        signal: req?.signal,
        timeoutMs: req?.timeoutMs,
      });
      if (!allowed) {
        throw createActionRpcError({
          reason: "no_capability",
          message: "Download was blocked by host",
        });
      }
    }

    // Convert ColumnDefinition to UnifiedTableColumn
    const unifiedColumns: UnifiedTableColumn<unknown>[] = columns.map(
      (col) => ({
        key: col.key,
        title: col.header,
        exportable: true,
      }),
    );

    // Log the download attempt
    if (onDownload) {
      onDownload(normalizedParams, data.length);
    }

    // Execute CSV export
    await exportToCSV(data, unifiedColumns, filename);

    if (onAfterDownload) {
      await onAfterDownload(normalizedParams, {
        method: DOWNLOAD_ACTION_METHOD,
        payload: params,
        signal: req?.signal,
        timeoutMs: req?.timeoutMs,
      });
    }

    return { success: true, rowCount: data.length };
  };
}
